"use client";
import { VeBox, VeTypography } from "@packages/ui/mui";
import { VE_ICON_GREY, VE_ICON_PURPLE } from "../../../public";
import Image from "next/image";
import { AuthForm, VeButton, VeInput } from "@packages/ui/components";
import styles from "./forgorPassword.module.scss";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { commonAction } from "../../../libs/actions/actions";
import { SubmitHandler, useForm, z, zodResolver } from "@packages/ui/libs";
import { useRouter } from "next/navigation";
import ReturnSignIn from "@components/returnSignIn/returnSignIn";
import { toast } from "@libs/helper/toast";
import AuthLinkDesc from "@components/AuthLinkDesc/authLinkDesc";
import { mutateAction } from "@libs/actions/mutateAction";

const validationSchema = z.object({
  email: z
    .string()
    .email("Please enter a valid email.")
    .min(1, "Email cannot be empty"),
});

type ValidationSchema = z.infer<typeof validationSchema>;

const page = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    mode: "all",
  });
  const router = useRouter();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: mutateAction,
    // mutationFn: commonAction,
    mutationKey: ["login-2fa"],
    onSuccess: async (data, variables) => {
      if (data.error)
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      queryClient.setQueryData(["forgot-pass-data"], {
        ...variables.data,
      });
      router.push("/reset-password");
    },

    onError: (error) => {
      toast(error?.cause as string, { type: "error" });
      setError("email", {
        type: "server",
        message: String(error?.cause ?? "Something went wrong"),
      });
    },
  });

  const onSubmit: SubmitHandler<ValidationSchema> = (data) => {
    mutate({
      type: "post",
      endPoint: "/auth/forgot-password",
      data: { email: data?.email?.toLowerCase() },
    });
  };

  return (
    <VeBox sx={{ maxWidth: "400px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AuthForm
          border={true}
          heading="Forgot Password"
          icon={<Image src={VE_ICON_PURPLE} alt="VE_LOGO" />}
          UnderHeading={true}
          UnderButton={<ReturnSignIn text="Return to sign in" link="/signIn" />}
        >
          <VeBox marginBottom={"32px"} textAlign={"left"}>
            <VeTypography variant="body2Main">
              Enter your registered email below and we will send a message to
              reset your password.
            </VeTypography>
          </VeBox>
          <VeBox width="100%">
            <VeInput
              label="Email"
              name="email"
              register={register}
              error={!!errors.email}
              errorText={errors.email?.message || ""}
            />
          </VeBox>
          <VeButton
            styles={{ marginTop: "32px" }}
            type="submit"
            buttonVariant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={!isValid}
            loading={isPending}
          >
            Send
          </VeButton>
        </AuthForm>
        <AuthLinkDesc />
      </form>
    </VeBox>
  );
};

export default page;
